import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Divider, MenuItem, Paper, Select, TextField, Typography } from "@mui/material";
import { GISTMRequisitos } from "../../services/api/gistm";
import { useState } from "react";
import { HidroDXEditor } from "../edit";
import { objectToHash } from "../../core";
import { RequestError, useApi } from "../../services";
import { ErrorOnDialog } from "../modals";

interface ItemProps {
  onFinish?: (checkitem: GISTMRequisitos) => void;
  onCancel?: () => void;
  barragem: number;
  initRequisito?: GISTMRequisitos;
}

export interface GISTMRequisitoModalProps extends DialogProps, ItemProps { }

export const GISTMRequisitoModal: React.FC<GISTMRequisitoModalProps> = ({ barragem, initRequisito, onFinish, onCancel, ...dialogProps }) => {
  const api = useApi();

  const [requisito, setRequisito] = useState<GISTMRequisitos>(initRequisito ?? {
    id: -1,
    idBarragem: barragem,
    principio: 1,
    requisito: 1,
    desc: "",
    aplicacao: false,
    documentacao: "sim",
    peso: 1,
    idUpdate: "",
    dataCadastro: new Date(),
  });
  const [error, setError] = useState<RequestError>();

  const onCancelar = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    ev.preventDefault();
    ev.stopPropagation();

    setRequisito({
      id: -1,
      idBarragem: barragem,
      principio: 1,
      requisito: 1,
      desc: "",
      aplicacao: false,
      documentacao: "sim",
      peso: 1,
      idUpdate: "",
      dataCadastro: new Date(),
    });

    onCancel?.();
  };

  const onEdit = (ev: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    ev.preventDefault();
    ev.stopPropagation();

    const onAdd = (value: RequestError | GISTMRequisitos) => {
      if ("error" in value) {
        setError(value);
        return;
      }
      setRequisito(value);
      onFinish?.(value);
    };

    if (requisito.id <= 0) {
      api.gistm.addRequisito(requisito.idBarragem, requisito).then(onAdd);
    } else {
      api.gistm.updateRequisito(requisito).then(onAdd);
    }
  };

  return <>

    {error && <ErrorOnDialog
      open={Boolean(error)}
      error={error.error}
      message={error.message}
      onClose={() => setError(undefined)}
    />}

    <Dialog {...dialogProps}
      sx={{ zIndex: 2 }}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
    >
      <DialogTitle id="dialog-title">
        {requisito.id > 0 ? "Editar" : "Adicionar"} Requisito
      </DialogTitle>

      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: "15px" }}>
        < Divider />
        <div style={{ height: "1em" }} />

        {/* Princípio e Requisito */}
        <Box sx={{
          flex: 1,
          gap: "0.5em",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}>
          <TextField
            fullWidth
            label="Princípio"
            value={requisito.principio > 0 ? requisito.principio : ""}
            onChange={(e) => {
              const val = parseInt(e.target.value.replaceAll(",", "."));
              if (Number.isNaN(val)) {
                setRequisito(prev => ({ ...prev, principio: 0 }));
                return;
              }
              setRequisito(prev => ({ ...prev, principio: val }));
            }}
          />
          <TextField
            fullWidth
            label="Requisito"
            value={requisito.requisito > 0 ? requisito.requisito : ""}
            onChange={(e) => {
              const val = parseInt(e.target.value.replaceAll(",", "."));
              if (Number.isNaN(val)) {
                setRequisito(prev => ({ ...prev, requisito: 0 }));
                return;
              }
              setRequisito(prev => ({ ...prev, requisito: val }));
            }}
          />
        </Box>

        {/* Aplicação */}
        <Box sx={{
          flex: 1,
          gap: "0.5em",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
        }}>
          <Typography>APLICAÇÃO DO REQUISITO:</Typography>
          <div style={{ flex: 1 }} />
          <Select
            size="small"
            value={String(requisito.aplicacao)}
            onChange={(ev) => setRequisito(prev => ({ ...prev, aplicacao: ev.target.value === "true" }))}
            sx={{
              fontWeight: "bold",
              color: requisito.aplicacao ? "success.main" : "error.main",
              minWidth: 150,
            }}
          >
            <MenuItem value="true">APLICÁVEL</MenuItem>
            <MenuItem value="false">NÃO APLICÁVEL</MenuItem>
          </Select>
        </Box>

        {/* Atende */}
        <Box sx={{
          flex: 1,
          gap: "0.5em",
          display: "flex",
          flexDirection: "row",
        }}>
          <Typography>DOCUMENTAÇÃO DISPONIBILIZADA ATENDE AO REQUISITO?</Typography>
          <div style={{ flex: 1 }} />
          <Select
            size="small"
            value={requisito.documentacao}
            onChange={(ev) => setRequisito(prev => ({
              ...prev,
              documentacao: (ev.target.value as ("sim" | "parcial" | "inexistente" | "não necessário")),
              peso: documentacaoMap((ev.target.value as ("sim" | "parcial" | "inexistente" | "não necessário")))[2] as number,
            }))}
            sx={{
              fontWeight: "bold",
              color: documentacaoMap(requisito.documentacao)[1] + ".main",
              minWidth: 150,
            }}
          >
            <MenuItem value="sim">ATENDE</MenuItem>
            <MenuItem value="parcial">ATENDE PARCIALMENTE</MenuItem>
            <MenuItem value="inexistente">NÃO EXISTENTE</MenuItem>
            <MenuItem value="não necessário">NÃO HÁ NECESSIDADE DE DOCUMENTAÇÃO</MenuItem>
          </Select>
        </Box>

        <Divider flexItem />

        {/* Descrição */}
        <Typography>Descrição:</Typography>
        <Paper elevation={1} style={{ textAlign: "justify" }}>
          <HidroDXEditor
            image={false}
            chartMap={false}
            startText={requisito.desc}
            onChange={(text) => setRequisito({ ...requisito, desc: text })}
          />
        </Paper>

        <Divider flexItem />

        {/* Observação HBR */}
        <Typography>Observação HIDROBR:</Typography>
        <Paper elevation={1} style={{ textAlign: "justify" }}>
          <HidroDXEditor
            image={false}
            chartMap={false}
            startText={requisito.obsHbr}
            onChange={(text) => setRequisito({ ...requisito, obsHbr: text })}
          />
        </Paper>

      </DialogContent>

      <DialogActions>
        <Button onClick={onCancelar} autoFocus>Cancelar</Button>
        <Button onClick={onEdit} autoFocus>{requisito.id > 0 ? "Editar" : "Adicionar"}</Button>
      </DialogActions>
    </Dialog >
  </>;
};


export interface GISTMRequisitoButtonProps extends ItemProps {
  title?: React.ReactNode;
  variant?: "text" | "outlined" | "contained";
}

export const GISTMRequisitoButton: React.FC<GISTMRequisitoButtonProps> = ({ title, variant, barragem, initRequisito, onFinish, onCancel }) => {

  const [open, setOpen] = useState(false);

  return <>
    {open && <GISTMRequisitoModal
      open={open}
      barragem={barragem}
      initRequisito={initRequisito}
      onFinish={(checkitem) => {
        setOpen(false);
        onFinish?.(checkitem);
      }}
      onCancel={() => {
        setOpen(false);
        onCancel?.();
      }}
    />}

    <Button fullWidth variant={variant} onClick={() => setOpen(true)}>
      {title ?? (initRequisito ? "Editar Requisito" : "Adicionar Requisito")}
    </Button>
  </>;
};


const documentacaoMap = (doc: "sim" | "parcial" | "inexistente" | "não necessário") => {
  switch (doc) {
    case "sim":
      return ["ATENDE", "sucess", 1];
    case "inexistente":
      return ["NÃO EXISTENTE", "error", 0];
    case "parcial":
      return ["ATENDE PARCIALMENTE", "info", 0.5];
    case "não necessário":
      return ["NÃO HÁ NECESSIDADE DE DOCUMENTAÇÃO", undefined, 0];
    default:
      return ["NÃO HÁ NECESSIDADE DE DOCUMENTAÇÃO", undefined, 0];
  }
};